import { ChangeDetectionStrategy, Component, inject, input, OnDestroy, OnInit } from '@angular/core';

import { BehaviorSubject, combineLatest, NEVER, Observable, of } from 'rxjs';
import { catchError, filter, map, shareReplay, switchMap, takeUntil, tap } from 'rxjs/operators';

import { CommonModule } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { BaseContainer } from '../../../core/common/containers/base.abstract.container';
import { ApiFragment } from '../../../core/records/entities/api-fragment.class';
import { FragmentId } from '../../../core/records/entities/metadata/metadata.entity';
import { ActionItem, ActionItemStyle } from '../../../elements/components/action-item/entities/action-item.entity';
import { NotificationType } from '../../../elements/components/notification/entities/notification-types.entity';
import { ShNotificationComponent } from '../../../elements/components/notification/notification.component';
import { EllipsisMultiActionItem } from '../../../helpers/actions/ellipsis-multi-action.helper';
import { LoadingBehaviorSubject } from '../../../helpers/loading-behavior-subject.helper';
import { PaginatedResults } from '../../../overviews/generic-overview/components/pagination/entities/paginated-results.entity';
import {
    defaultPaginationInfo,
    PaginationInfo
} from '../../../overviews/generic-overview/components/pagination/entities/pagination-info.entity';
import { ShGenericOverviewContainerComponent } from '../../../overviews/generic-overview/containers/generic-overview/generic-overview.container.component';
import { ShGenericListComponent } from '../../../overviews/list-view/containers/generic-list/generic-list.component';
import { SearchBarMode } from '../../../overviews/list-view/entities/filter-bar-mode.entity';
import { getShareContext } from '../../../session-storage/helpers/fetch-from-session-storage/session-storage.helper';
import { FragmentItem } from '../../entities/fragment-item.entity';
import { RecordDetailSandbox } from '../../record-detail.sandbox';
import { FragmentColumn, FragmentColumnService } from '../../services/fragment-column.service';

@Component({
    standalone: true,
    selector: 'sh-record-fragments-tab',
    templateUrl: './record-fragments-tab-container.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        CommonModule,
        ShGenericListComponent,
        ShGenericOverviewContainerComponent,
        ShNotificationComponent,
        TranslateModule
    ]
})
export class ShRecordFragmentsTabContainerComponent extends BaseContainer implements OnInit, OnDestroy {
    private readonly childFragmentItemsPagination$: BehaviorSubject<PaginationInfo> =
        new BehaviorSubject<PaginationInfo>(defaultPaginationInfo);

    private pureFragment$: Observable<ApiFragment>;
    private mainFragmentId$: Observable<FragmentId>;

    public readonly loadingError$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    public readonly isLoading$: LoadingBehaviorSubject = new LoadingBehaviorSubject(true);

    public readonly columns: FragmentColumn[] = [FragmentColumnService.MEDIA, FragmentColumnService.CURRENT_ITEM];

    public hasFragments$: Observable<boolean>;
    public hasVisibleFragments$: Observable<boolean>;
    public mainFragmentItem$: Observable<FragmentItem[]>;
    public mainFragmentItemActions$: Observable<ActionItem[]>;
    public paginatedChildFragmentItems$: Observable<PaginatedResults<FragmentItem>>;
    public childFragmentItemsActions$: Observable<ActionItem[]>;
    public currentFragmentItem$: Observable<FragmentItem>;

    public notificationType: typeof NotificationType = NotificationType;
    public searchBarMode: SearchBarMode = SearchBarMode.NONE;

    record = input.required<ApiFragment>();

    private readonly sandbox: RecordDetailSandbox = inject(RecordDetailSandbox);
    private readonly route: ActivatedRoute = inject(ActivatedRoute);

    constructor(public fragmentColumnService: FragmentColumnService) {
        super();
    }

    ngOnInit(): void {
        this.initStateStreams();
        this.initSourceStreams();
        this.watchLoadingState();
    }

    ngOnDestroy(): void {
        super.ngOnDestroy();

        this.loadingError$.complete();
        this.isLoading$.complete();
    }

    public onChildFragmentItemsPaginationInfoChanged(pagination: PaginationInfo): void {
        this.childFragmentItemsPagination$.next(pagination);
    }

    private initStateStreams(): void {
        this.pureFragment$ = this.createPureFragmentStream();
        this.hasFragments$ = this.createHasFragmentsStream();
    }

    private initSourceStreams(): void {
        this.mainFragmentId$ = this.createMainFragmentIdStream();
        this.mainFragmentItem$ = this.createMainFragmentItemStream();
        this.mainFragmentItemActions$ = this.createMainFragmentItemActionsStream();

        this.paginatedChildFragmentItems$ = this.createChildFragmentItemsStream();

        this.hasVisibleFragments$ = this.createHasVisibleFragmentsStream();

        this.childFragmentItemsActions$ = this.createChildFragmentItemsActionsStream();

        this.currentFragmentItem$ = this.createCurrentFragmentItemStream();
    }

    // #region State
    private createPureFragmentStream(): Observable<ApiFragment> {
        return of(this.record()).pipe(
            filter((fragment: ApiFragment | null): fragment is ApiFragment => !!fragment),
            tap(() => this.isLoading$.startLoading()),
            shareReplay(1)
        );
    }

    private createHasFragmentsStream(): Observable<boolean> {
        return this.pureFragment$.pipe(map(this.isFragmentPartOfHierarchy.bind(this)), shareReplay(1));
    }

    private createHasVisibleFragmentsStream(): Observable<boolean> {
        return this.paginatedChildFragmentItems$.pipe(
            map(results => results.paginationInfo.totalNumberOfResults > 0),
            shareReplay(1)
        );
    }

    private isFragmentPartOfHierarchy(fragment: ApiFragment): boolean {
        const hasMainFragment: boolean = fragment.getMainFragmentId() !== null;
        const hasChildFragments: boolean = fragment.getChildFragmentsIds().length > 0;
        return hasMainFragment || hasChildFragments;
    }
    // #endregion

    // #region Main Hierarchy Item
    private createMainFragmentIdStream(): Observable<FragmentId> {
        return this.pureFragment$.pipe(
            filter(this.isFragmentPartOfHierarchy.bind(this)),
            map((fragment: ApiFragment) => {
                const mainFragmentId: FragmentId | null = fragment.getMainFragmentId();
                return mainFragmentId ? mainFragmentId : fragment.getFragmentId();
            }),
            shareReplay(1)
        );
    }

    private createMainFragmentItemStream(): Observable<FragmentItem[]> {
        return combineLatest([this.pureFragment$, this.mainFragmentId$]).pipe(
            switchMap(([currentFragment, mainFragmentId]: [ApiFragment, FragmentId]) =>
                combineLatest([this.getMainFragmentById(currentFragment, mainFragmentId), of(currentFragment)])
            ),
            map(([mainFragment, currentFragment]: [ApiFragment, ApiFragment]) => [
                this.recordToFragmentItem(mainFragment, currentFragment)
            ]),
            catchError(() => this.setLoadingError()),
            shareReplay(1)
        );
    }

    private getMainFragmentById(currentFragment: ApiFragment, mainFragmentId: FragmentId): Observable<ApiFragment> {
        return mainFragmentId === currentFragment.getFragmentId()
            ? of(currentFragment)
            : this.sandbox.getRecord(mainFragmentId);
    }

    private createMainFragmentItemActionsStream(): Observable<ActionItem[]> {
        return combineLatest([this.mainFragmentItem$, getShareContext(this.route)]).pipe(
            map(([items, shareContext]: [FragmentItem[], string | undefined]) =>
                items.map(item => this.getFragmentItemAction(item, shareContext))
            ),
            shareReplay(1)
        );
    }
    // #region

    // #region Child Hierarchy Items
    private createChildFragmentItemsStream(): Observable<PaginatedResults<FragmentItem>> {
        return combineLatest([this.pureFragment$, this.mainFragmentId$]).pipe(
            switchMap(([currentFragment, mainFragmentId]: [ApiFragment, FragmentId]) =>
                combineLatest([this.getChildFragmentsFromMainFragmentId(mainFragmentId), of(currentFragment)])
            ),
            map(([childFragments, currentFragment]: [PaginatedResults<ApiFragment>, ApiFragment]) => {
                return {
                    ...childFragments,
                    results: childFragments.results.map((childFragment: ApiFragment) => {
                        return this.recordToFragmentItem(childFragment, currentFragment);
                    })
                };
            }),
            catchError(() => this.setLoadingError()),
            shareReplay(1)
        );
    }

    private getChildFragmentsFromMainFragmentId(mainFragmentId: FragmentId): Observable<PaginatedResults<ApiFragment>> {
        return this.childFragmentItemsPagination$.pipe(
            switchMap((paginationInfo: PaginationInfo) => {
                return this.sandbox.getMainFragmentChildRecords(mainFragmentId, paginationInfo);
            })
        );
    }

    private createChildFragmentItemsActionsStream(): Observable<ActionItem[]> {
        return combineLatest([this.paginatedChildFragmentItems$, getShareContext(this.route)]).pipe(
            map(([paginatedResult, shareContext]: [PaginatedResults<FragmentItem>, string | undefined]) =>
                paginatedResult.results.map(item => this.getFragmentItemAction(item, shareContext))
            ),
            shareReplay(1)
        );
    }
    // #endregion

    // #region Current Item
    private createCurrentFragmentItemStream(): Observable<FragmentItem> {
        return this.pureFragment$.pipe(
            map(
                currentFragment =>
                    new FragmentItem({
                        fragment: currentFragment,
                        isCurrent: true
                    })
            )
        );
    }
    // #endregion

    // #region Generic Helpers
    private recordToFragmentItem(fragment: ApiFragment, currentFragment: ApiFragment): FragmentItem {
        return new FragmentItem({
            fragment,
            isCurrent: fragment.getFragmentId() === currentFragment.getFragmentId()
        });
    }

    private getFragmentItemAction(item: FragmentItem, shareContext?: string): ActionItem {
        const view: ActionItem = {
            label: this.translateService.instant('actions.watch'),
            navigationLink: {
                route: shareContext ? item.fragment.getShareDetailUrl(shareContext) : item.fragment.getDetailUrl()
            }
        };

        return EllipsisMultiActionItem([view], false, ActionItemStyle.PureIconButton);
    }
    // #endregion

    // #region loading
    private setLoadingError(): Observable<never> {
        this.loadingError$.next(true);
        this.isLoading$.stopLoading();

        return NEVER;
    }

    private watchLoadingState(): void {
        this.hasFragments$.pipe(takeUntil(this.destroy$)).subscribe((hasFragments: boolean) => {
            if (!hasFragments) {
                this.isLoading$.stopLoading();
            }
        });

        combineLatest([this.mainFragmentItem$, this.paginatedChildFragmentItems$])
            .pipe(takeUntil(this.destroy$))
            .subscribe(([mainFragment, childFragments]: [FragmentItem[], PaginatedResults<FragmentItem>]) => {
                if (mainFragment.length > 0 && childFragments.results) {
                    this.isLoading$.stopLoading();
                }
            });
    }
    // #endregion
}
